import { Suspense, useContext, useEffect } from "react";
import lazyWithRetry from "../../utils/lazyWithRetry";
import Typography from "../../component/reusableComponent/typography/Typography";
import styles from "./onBoard.module.scss";
import { useOnBoarding } from "./hooks/useOnBoarding";
import AppButton from "../../component/reusableComponent/button/button";
import Progress from "../../component/onBoarding&Preferences/onBoarding/components/progress/progress";
import LookingFor from "../../component/onBoarding&Preferences/onBoarding/components/lookingFor/lookingFor";
import { StateContext } from "../../context/globalContext/context";
import { iGlobalContext } from "../../context/globalContext/interface";
import { isEmbedMode } from "../../utils/embedMode";
import { funnelBrandClass } from "../../constant/funnelAssets";

// LookingFor is the first paint on /onboarding (entry step) — keep eager.
// Other step components are code-split and prefetched in idle time below.
const HomeCity = lazyWithRetry(() => import("../../component/onBoarding&Preferences/onBoarding/components/homeCity/homeCity"));
const TravelWith = lazyWithRetry(
  () => import("../../component/onBoarding&Preferences/onBoarding/components/travelWith/travelWith")
);
const Budget = lazyWithRetry(() => import("../../component/onBoarding&Preferences/onBoarding/components/budget/budget"));
const OnBoardingSingUp = lazyWithRetry(
  () => import("../../component/onBoarding&Preferences/onBoarding/components/singUp/singUp")
);

function OnBoarding(): JSX.Element {
  const { userInfo }: iGlobalContext = useContext(StateContext);
  const { isMobile, onBoardingStep, progress, disableNext, templateDetails, handleNext, handleBack, handleLogin } =
    useOnBoarding();

  // Eagerly fetch the login Dialog chunk on mount. lookingFor is the first
  // screen and the user may click "Have an account? Log in" almost immediately
  // — idle-callback prefetch can lose that race, so kick the import off now.
  useEffect(() => {
    import("../../component/home&Landingpage/mainPageApp/dialog/Dialog").catch(() => {});
  }, []);

  // Prefetch the next-step chunk during the current step's idle time so the
  // Next button feels instant once the user advances.
  useEffect(() => {
    const prefetch = (load: () => Promise<unknown>) => {
      const run = () => {
        load().catch(() => {});
      };
      if (typeof window === "undefined") return;
      if ("requestIdleCallback" in window) {
        (window as typeof window & { requestIdleCallback: (cb: () => void) => number }).requestIdleCallback(run);
      } else {
        setTimeout(run, 200);
      }
    };
    switch (onBoardingStep) {
      case "lookingFor":
        prefetch(() => import("../../component/onBoarding&Preferences/onBoarding/components/travelWith/travelWith"));
        break;
      case "travelWith":
        prefetch(() => import("../../component/onBoarding&Preferences/onBoarding/components/budget/budget"));
        break;
      case "budget":
        prefetch(() => import("../../component/onBoarding&Preferences/onBoarding/components/homeCity/homeCity"));
        break;
      case "home":
        prefetch(() => import("../../component/onBoarding&Preferences/onBoarding/components/singUp/singUp"));
        break;
      default:
        break;
    }
  }, [onBoardingStep]);

  const renderStep = (): JSX.Element => {
    switch (onBoardingStep) {
      case "lookingFor":
        return <LookingFor />;
      case "travelWith":
        return <TravelWith />;
      case "budget":
        return <Budget />;
      case "home":
        return <HomeCity />;
      case "singUp":
        return <OnBoardingSingUp />;
      default:
        return <LookingFor />;
    }
  };

  const singUpStep = onBoardingStep === "singUp";
  const showProgressBar = !singUpStep;
  const isFirstStep = onBoardingStep === "lookingFor";

  // Logged-in user landed on /onboarding directly — useOnBoarding's mount effect queues a navigate to Home. Render nothing in the meantime so the LookingFor first step doesn't paint for one frame before the redirect. App.tsx defers the HTML boot loader removal for this path, so the user sees a continuous fox loader across the redirect instead of a flash.
  if (userInfo?.loggedIn || isEmbedMode()) return <></>;

  return (
    <div className={`${funnelBrandClass} ${!isMobile ? styles.desktopView : styles.mobileView}`}>
      <div className={`${singUpStep ? styles.lastContainer : styles.onboardingContainer}`}>
        {showProgressBar && <Progress value={progress} />}

        <div className={`${singUpStep ? styles.singUpCard : styles.content}`}>
          <Suspense fallback={null}>{renderStep()}</Suspense>
        </div>

        {!singUpStep && (
          <div className={styles.div}>
            {isFirstStep ? (
              <>
                <Typography color="hyperlink" className={styles.loginLink} onClick={handleLogin}>
                  {templateDetails?.welcome?.loginLink ?? "Have an account? Log in"}
                </Typography>
                <AppButton size="full_width" onClick={handleNext} disabled={disableNext}>
                  Next
                </AppButton>
              </>
            ) : (
              <div className={styles.btnCard2}>
                <AppButton variant="secondary" onClick={handleBack}>
                  Back
                </AppButton>
                <AppButton size="full_width" onClick={handleNext} disabled={disableNext}>
                  Next
                </AppButton>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default OnBoarding;
